import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectConfig } from '../../redux/slices/configSlice';
import type { Conversation } from '@twilio/conversations';

interface UsernameInputProps {
  onSubmit?: (username: string) => void;
  conversation?: Conversation;
}

const UsernameInput: React.FC<UsernameInputProps> = ({ onSubmit, conversation }) => {
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const config = useSelector(selectConfig);
  const RmConfigs = config?.data?.[0]?.attributes.RichMediaConfigs;

  const validateUsername = (value: string): string => {
    const usernameRegex = /^[a-zA-Z0-9]{3,15}$/;

    if (value.length < 3 || value.length > 15) {
      return RmConfigs?.RichmediaUsernameCharacterLimitErrorMessage || 'Username must be 3-15 characters';
    }

    if (!usernameRegex.test(value)) {
      return RmConfigs?.RichmediaUsernameUnsupportedCharacterErrorMessage || 'Username can only contain letters and numbers';
    }

    return '';
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUsername(value);
    setError(validateUsername(value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateUsername(username);
    if (!validationError) {
      setIsSubmitted(true);
      console.log('Submitted username:', username);

      // Send username as message using conversation.sendMessage
      if (conversation) {
        try {
          await conversation.sendMessage(username);
          console.log('✅ Username sent successfully:', username);
        } catch (error) {
          console.error('❌ Failed to send username:', error);
          // Re-enable form if sending failed
          setIsSubmitted(false);
          return;
        }
      }

      // Call optional onSubmit callback
      if (onSubmit) {
        onSubmit(username);
      }
    } else {
      setError(validationError);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: 'flex',
        flexFlow: 'column',
        width: '100%',
        maxWidth: '300px',
        textAlign: 'left',
        background: '#e5e5ea',
        padding: '16px',
        borderRadius: '12px',
        margin: '8px 0',
        gap: '12px'
      }}
    >
      <input
        type="text"
        id="username"
        value={username}
        onChange={handleChange}
        placeholder={RmConfigs?.RichmediaUsernamePlaceholder || 'Enter username'}
        disabled={isSubmitted}
        style={{
          width: '100%',
          padding: '10px 14px',
          borderRadius: '8px',
          border: '1px solid #ccc',
          fontSize: '14px',
          height: '40px',
          boxSizing: 'border-box',
          outline: 'none',
          opacity: isSubmitted ? 0.6 : 1
        }}
      />
      {error && <p style={{ color: 'red', fontSize: '12px', margin: '0 0 8px 0' }}>{error}</p>}

      <button
        type="submit"
        disabled={!!error || username === '' || isSubmitted}
        style={{
          border: "none",
          borderRadius: "20px",
          padding: "10px 20px",
          width: "100%",
          height: "40px",
          backgroundColor: isSubmitted || !!error || username === '' ? "#cccccc" : "#af0000",
          color: isSubmitted || !!error || username === '' ? "#666666" : "#fff",
          fontSize: "14px",
          fontWeight: "bold",
          cursor: isSubmitted || !!error || username === '' ? "not-allowed" : "pointer",
          transition: "all 0.2s ease",
          opacity: isSubmitted ? 0.6 : 1
        }}
      >
        提交
      </button>
    </form>
  );
};

export default UsernameInput;
